"use client";

import { SHOW_OPEN_TO_WORK_BANNER } from "@/assets/static/constants";
import ArrowBack from "@/assets/vectors/ArrowBack";
import DotScope from "@/assets/vectors/DotScope";
import TailLeft from "@/assets/vectors/TailLeft";
import TailRight from "@/assets/vectors/TailRight";
import Xmark from "@/assets/vectors/Xmark";
import { useAudioVisualization } from "@/context/AudioVisualizationContext";
import { useNavigationItems } from "@/context/NavigationItemsContext";
import dictionary from "@/utils/dictionary";
import clsx from "clsx";
import { animate, AnimatePresence, motion, useMotionValue, useMotionValueEvent, useReducedMotion, useScroll, useSpring, useTransform } from "framer-motion";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { useEffect, useRef, useState, useSyncExternalStore } from "react";
import EmranLogo from '../../assets/vectors/EmranLogo';
import NavigationCallToAction from "./_components/navigation_call_to_action/NavigationCallToAction";
import CommandPalette from "@/components/command_palette/CommandPalette";
import styles from "./navigation_bar.module.scss";

const subscribeToBanner = (callback: () => void) => {
    window.addEventListener('storage', callback);
    window.addEventListener('banner-dismissed', callback);
    return () => {
        window.removeEventListener('storage', callback);
        window.removeEventListener('banner-dismissed', callback);
    };
};
const getBannerDismissed = () => localStorage.getItem('open_to_work_dismissed') !== null;
const getServerBannerDismissed = () => true;

export default function NavigationBar() {
    const pathname = usePathname();
    return pathname === '/resume' ? null : <NavigationBarContent pathname={pathname} />;
}

function NavigationBarContent({ pathname }: { pathname: string }) {
    const isHome = pathname === '/';
    const dismissed = useSyncExternalStore(subscribeToBanner, getBannerDismissed, getServerBannerDismissed);
    const bannerVisibility = SHOW_OPEN_TO_WORK_BANNER && isHome && !dismissed;

    const { items, isDefault } = useNavigationItems()
    const [activeItems, setActiveItems] = useState<string[]>([])
    const breadcrumbs = pathname.split("/").filter(Boolean);
    const [breadcrumbsWidth, setBreadcrumbsWidth] = useState<number | undefined>(undefined)
    const breadcrumbsRef = useRef<HTMLUListElement>(null)
    const shouldReduceMotion = useReducedMotion();

    // Audio visualization
    const { isPlaying, beatIntensity, bassEnergy, isBeat, bpmEstimate, visualizerSensitivity, visualizerRange, playbackCurrentTime, playbackDuration, playbackProgress } = useAudioVisualization();

    // Natural layered animation: base "breath" + sharp beat pulse
    const bassValue = useSpring(0, { stiffness: 260, damping: 22, mass: 0.5 });
    const breathScale = useTransform(bassValue, (v) => shouldReduceMotion ? 1 : 1 + v * 1);
    const rotate = useTransform(bassValue, (v) => (shouldReduceMotion || !isPlaying) ? 0 : (v - 0.5) * 14);

    // Beat pulse layer
    const beatScale = useMotionValue(1);
    const combinedScale = useTransform([breathScale, beatScale], (values) => (values[0] as number) * (values[1] as number));

    // Scroll-based logo morphing
    const { scrollY } = useScroll();
    const scrollProgress = useMotionValue(0);

    useMotionValueEvent(scrollY, "change", (latest) => {
        const maxScroll = typeof document !== "undefined" ? document.body.scrollHeight - window.innerHeight : 1000;
        const progress = Math.min(1, Math.max(0, latest / (maxScroll || 1)));
        scrollProgress.set(progress);
    });

    const scrollLogoRotate = useTransform(scrollProgress, [0, 0.5, 1], shouldReduceMotion ? [0, 0, 0] : [0, 15, 30]);
    const combinedRotate = useTransform([rotate, scrollLogoRotate], ([audioRot, scrollRot]) => (audioRot as number) + (scrollRot as number));

    useEffect(() => {
        if (!isPlaying || shouldReduceMotion) {
            bassValue.set(0);
            return;
        }
        bassValue.set(bassEnergy);
    }, [bassEnergy, isPlaying, bassValue]);

    const beatAnimationRef = useRef<ReturnType<typeof animate> | null>(null);
    const lastBassRef = useRef<number>(0);
    const lastFallbackPulseRef = useRef<number>(0);

    useEffect(() => {
        if (!isPlaying || shouldReduceMotion) {
            beatScale.set(1);
            beatAnimationRef.current?.stop();
            beatAnimationRef.current = null;
            return;
        }
        if (isBeat) {
            beatAnimationRef.current?.stop();
            const clamped = Math.min(Math.max(beatIntensity, 0), 1);
            const sens = Math.max(0.5, Math.min(visualizerSensitivity ?? 1, 2));
            const bpm = bpmEstimate ?? 120;
            const tempoFactor = Math.max(0.8, Math.min(bpm / 120, 1.4));
            const softplus = Math.log(1 + Math.exp(6 * clamped)) / 6;
            const smooth = clamped * clamped * (3 - 2 * clamped);
            let lifted = 0.40 * softplus + 0.60 * smooth;
            const compress = (x: number) => {
                const range = Math.max(0.5, Math.min(visualizerRange ?? 1, 2));
                const k = 0.82 + (1 - range) * 0.12;
                const floor = 0.35 + (1 - range) * 0.10;
                const mixed = floor + (1 - floor) * Math.pow(x, k);
                return 0.25 + 0.75 * mixed;
            };
            lifted = compress(lifted);
            const base = 0.16 * sens * (1 / tempoFactor) + (0.08 + (1 - Math.max(0.5, Math.min(visualizerRange ?? 1, 2))) * 0.06);
            const gain = (0.30 + (Math.max(0.5, Math.min(visualizerRange ?? 1, 2)) - 1) * 0.16) * sens * tempoFactor + 0.16;
            const peak = 1 + base + gain * lifted;
            const pulseDuration = 0.36 * (1 / tempoFactor) + (1 - clamped) * 0.10;
            beatAnimationRef.current = animate(beatScale, [1, peak, 1], {
                duration: pulseDuration,
                ease: [0.18, 0.7, 0.28, 1],
                times: [0, 0.28, 1],
                onComplete: () => {
                    beatAnimationRef.current = null;
                },
            });
        }
    }, [isBeat, beatIntensity, isPlaying, bpmEstimate, visualizerSensitivity]);

    useEffect(() => {
        if (!isPlaying || shouldReduceMotion) return;
        const now = performance.now();
        const delta = bassEnergy - lastBassRef.current;
        lastBassRef.current = bassEnergy;
        const idleForMs = now - lastFallbackPulseRef.current;
        const risingQuickly = delta > 0.08;
        const enoughIdle = idleForMs > 350;
        if (!isBeat && risingQuickly && enoughIdle && !beatAnimationRef.current) {
            lastFallbackPulseRef.current = now;
            const sens = Math.max(0.5, Math.min(visualizerSensitivity ?? 1, 2));
            const peak = 1.08 + Math.min(bassEnergy * (0.13 * sens), 0.22);
            animate(beatScale, [1, peak, 1], {
                duration: 0.28,
                ease: [0.25, 0.6, 0.2, 1],
                times: [0, 0.4, 1],
            });
        }
    }, [bassEnergy, isBeat, isPlaying, beatScale, visualizerSensitivity]);

    useEffect(() => {
        if (!isPlaying || shouldReduceMotion) return;
        const id = setInterval(() => {
            const current = beatScale.get();
            const target = 1;
            const diff = target - current;
            if (Math.abs(diff) > 0.002 && !beatAnimationRef.current) {
                beatScale.set(current + diff * 0.12);
            }
        }, 32);
        return () => clearInterval(id);
    }, [isPlaying]);

    useEffect(() => {
        const element = breadcrumbsRef.current;
        if (!element) return;

        const observer = new ResizeObserver(entries => {
            for (const entry of entries) {
                if (entry.target === element) {
                    const width = entry.contentRect.width;
                    setBreadcrumbsWidth(width);
                }
            }
        });

        observer.observe(element);
        setBreadcrumbsWidth(element.getBoundingClientRect().width);

        return () => {
            observer.disconnect();
        };
    }, [breadcrumbs.length, breadcrumbsRef])

    useEffect(() => {
        if (typeof window === 'undefined') return;

        const visible = new Set<string>();
        const elements = items.flatMap(item => {
            if (!item.href?.includes('#')) return [];
            const element = document.getElementById(item.href.split('#')[1]);
            return element ? [{ element, href: item.href }] : [];
        });
        const links = new Map<Element, string>(elements.map(({ element, href }) => [element, href]));
        const observer = new IntersectionObserver(entries => {
            for (const entry of entries) {
                const href = links.get(entry.target);
                if (!href) continue;
                if (entry.isIntersecting) visible.add(href);
                else visible.delete(href);
            }
            setActiveItems(items.flatMap(item => item.href && visible.has(item.href) ? [item.href] : []));
        }, { rootMargin: '-100px 0px -50% 0px' });
        elements.forEach(({ element }) => observer.observe(element));
        return () => observer.disconnect();
    }, [items]);

    useEffect(() => {
        document.documentElement.style.setProperty('--banner-height', bannerVisibility ? '40px' : '0px');
        return () => { document.documentElement.style.removeProperty('--banner-height'); };
    }, [bannerVisibility]);

    const handleDismissBanner = (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        localStorage.setItem("open_to_work_dismissed", "true");
        window.dispatchEvent(new Event("banner-dismissed"));
        document.documentElement.style.setProperty("--banner-height", "0px");
    };

    const mobileTitle = breadcrumbs.length > 0
        ? breadcrumbs[breadcrumbs.length - 1].replace(/-/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())
        : "Emran";

    return (
        <AnimatePresence mode="wait">
            <header className={styles.container}>
                {/* Mobile app bar top row */}
                <div className={styles.mobile_header}>
                    {!isHome && (
                        <Link href="/" className={styles.mobile_back} aria-label="Back to home">
                            <ArrowBack className={styles.mobile_back_icon} />
                        </Link>
                    )}
                    <EmranLogo className={styles.logo} />
                    <span className={styles.mobile_title}>{mobileTitle}</span>
                </div>

                {/* Desktop logo + breadcrumbs */}
                <motion.a
                    href={"/"}
                    aria-label="Home"
                    className={clsx(styles.navigation_bar, styles.logo_container)}
                    animate={{ width: (breadcrumbsWidth ? breadcrumbsWidth + 42 : 42) }}
                    transition={shouldReduceMotion ? { duration: 0 } : {
                        type: "spring",
                        stiffness: 230,
                        damping: 14,
                    }}
                >
                    <motion.div
                        style={{
                            scale: combinedScale,
                            rotate: combinedRotate,
                            transformOrigin: "center",
                        }}
                        className={styles.logo_wrapper}
                    >
                        <EmranLogo className={styles.logo} />
                    </motion.div>
                    <motion.div
                        className={styles.breadcrumb_container}
                        initial="invisible"
                        variants={{
                            visible: { opacity: 1, transition: { duration: 0.6, ease: [0.2, 0.8, 0.2, 1] } },
                            invisible: { opacity: 0, transition: { duration: 0.6, ease: [0.2, 0.8, 0.2, 1] } },
                        }}
                        animate={breadcrumbs.length > 0 ? "visible" : "invisible"}
                        exit="invisible"
                    >
                        <AnimatePresence mode="wait">
                            <motion.ul
                                key="breadcrumbs"
                                className={styles.breadcrumb_list}
                                initial="hidden"
                                animate="visible"
                                exit="exit"
                                ref={breadcrumbsRef}
                                transition={{ duration: 0.6, ease: [0.2, 0.8, 0.2, 1] }}
                            >
                                {breadcrumbs.map((segment, index) => (
                                    <motion.li
                                        key={segment}
                                        className={clsx(styles.dynamic_route, index == breadcrumbs.length - 1 ? styles.last_segment : undefined)}
                                        variants={{
                                            visible: (index: number) => ({
                                                opacity: 1,
                                                y: 0,
                                                transition: { type: "spring", damping: 14, stiffness: 230, delay: (index + 1) * 0.04 },
                                            }),
                                            hidden: () => ({
                                                opacity: 0,
                                                y: isDefault ? -20 : 20,
                                            }),
                                            exit: () => ({
                                                opacity: 0,
                                                y: isDefault ? 20 : -20,
                                                transition: { duration: 0.2 },
                                            })
                                        }}
                                        custom={index}
                                        initial="hidden"
                                        animate="visible"
                                        exit="exit"
                                    >
                                        <span className={styles.breadcrumb_separator} />
                                        <span>
                                            {segment.replace(/-/g, " ").slice(0, 10)}
                                            {segment.replace(/-/g, " ").length > 10 ? "..." : ""}
                                        </span>
                                    </motion.li>
                                ))}
                            </motion.ul>
                        </AnimatePresence>
                    </motion.div>
                </motion.a>

                {/* Nav items */}
                <motion.nav
                    className={clsx(styles.navigation_bar, styles.main)}
                    animate={{ width: `${(items.length * 120) + 12}px` }}
                    transition={shouldReduceMotion ? { duration: 0 } : {
                        type: "spring",
                        stiffness: 230,
                        damping: 14,
                    }}
                ><AnimatePresence>
                        {isHome && bannerVisibility ?
                            <div className={styles.banner_wrapper}>
                                <motion.div
                                    className={styles.content}
                                    initial={{ opacity: 0, y: -40 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    exit={{ opacity: 0, y: -40 }}
                                    transition={{ type: "spring", stiffness: 200, damping: 20 }}>
                                    <TailLeft className={clsx(styles.tail, styles.left)} />
                                    <div className={styles.banner}>
                                        <a href="#section_contact" className={styles.banner_link}>
                                            <DotScope className={styles.icon} />
                                            <span className={styles.text} dangerouslySetInnerHTML={{ __html: dictionary.global.open_to_work }} />
                                        </a>
                                        <button
                                            className={styles.close_button}
                                            onClick={handleDismissBanner}
                                            aria-label="Dismiss banner"
                                        >
                                            <Xmark />
                                        </button>
                                    </div>
                                    <TailRight className={clsx(styles.tail, styles.right)} />
                                </motion.div>
                            </div>
                            : undefined}
                    </AnimatePresence>

                    <AnimatePresence mode="wait">
                        <motion.ul
                            key={pathname}
                            className={styles.navigation_list}
                            variants={{
                                visible: {
                                    opacity: 1,
                                    transition: {
                                        staggerChildren: 0.05,
                                    },
                                },
                                hidden: {
                                    opacity: 0,
                                },
                            }}
                            initial="hidden"
                            animate="visible"
                        >
                            {items.map((item, index) => (
                                <motion.li
                                    key={item.href}
                                    variants={{
                                        visible: {
                                            opacity: 1,
                                            y: 0,
                                            transition: { type: "spring", damping: 14, stiffness: 230, delay: (index + 1) * 0.04 },
                                        },
                                        hidden: {
                                            opacity: 0,
                                            y: isDefault ? -20 : 20,
                                        },
                                    }}
                                    className={clsx(styles.navigation_item, activeItems.includes(item.href) ? styles.selected : undefined)}
                                    exit={{ opacity: 0, y: isDefault ? 20 : -20 }}
                                >
                                    <Link href={item.href}>{item.label}</Link>
                                </motion.li>
                            ))}
                        </motion.ul>
                    </AnimatePresence>

                    {playbackDuration > 0 ? (
                        <motion.div
                            className={styles.progress}
                            role="progressbar"
                            aria-label="Playback progress"
                            aria-valuemin={0}
                            aria-valuemax={playbackDuration}
                            aria-valuenow={playbackCurrentTime}
                            initial={{ opacity: 0, y: 5 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: 5 }}
                            transition={{ type: "spring", stiffness: 230, damping: 18 }}
                        >
                            <div
                                className={styles.progress_fill}
                                style={{ width: `${Math.min(100, Math.max(0, playbackProgress * 100))}%` }}
                            />
                        </motion.div>
                    ) : null}
                </motion.nav>

                <CommandPalette />
                <NavigationCallToAction />
            </header>
        </AnimatePresence>
    );
}
